import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en';
import { $remoteConfigStore, RemoteConfig } from '@lib/store';
import {
  InquiryPreferredMethod,
  InquiryStatus,
  InquiryUrgency,
  LifestyleCategoryType,
  type GetSpecialOffersResponse,
  type InquiryJson,
  type LifestyleCategories,
} from '@shared/api';

dayjs.extend(utc);

const MAP_TO_OFFER_TYPE: Record<LifestyleCategoryType, string[]> = {
  hotels: ['Hotel'],
  flights: ['Airline tickets'],
  transfers: ['Transfer'],
  restaurants: ['Restaurant'],
  'events-and-tickets': ['Event', 'Tickets - concerts', 'Tickets - social events', 'Tickets - sport events', 'Tickets - theatre'],
  'private-jets': ['Jet'],
  'wellness-and-beauty': ['Wellness Card'],
  shopping: ['Shopping'],
  flowers: ['Flowers'],
  'vip-meet-and-greet': ['VIP Airport'],
  other: [],
};

const MAP_TO_LSB_REQUEST_TYPE: Record<LifestyleCategoryType, string> = {
  hotels: 'hotel',
  flights: 'airline tickets',
  transfers: 'Transfer',
  restaurants: 'restaurant',
  'events-and-tickets': 'event',
  'private-jets': 'jet',
  'wellness-and-beauty': 'wellness card',
  shopping: 'shopping',
  flowers: 'flowers',
  'vip-meet-and-greet': 'vip airport',
  other: 'Profit making Travel',
};

export class LsbUtils {
  static getLifestyleCategories = (): LifestyleCategories =>
    $remoteConfigStore.events.getValue<LifestyleCategories>(RemoteConfig.LifestyleCategories);

  static getLifestyleCategoryByType = (type: LifestyleCategoryType) => this.getLifestyleCategories().find((category) => category.type === type);

  static getSpecialOffersByType = (data: { type: LifestyleCategoryType; offers: GetSpecialOffersResponse[] | null }) => {
    const types = MAP_TO_OFFER_TYPE[data.type] ?? [];
    return data.offers?.filter((offer) => types.some((type) => type.toLowerCase() === String(offer.type).toLowerCase()));
  };

  static getSpecialOfferById = (data: { id: string; offers: GetSpecialOffersResponse[] | null }) =>
    data.offers?.find((offer) => offer.id === data.id);

  static getLsbRequestType = (type: LifestyleCategoryType) => MAP_TO_LSB_REQUEST_TYPE[type];

  static getUrgencyLabel = (urgency: InquiryUrgency) => {
    switch (urgency) {
      case InquiryUrgency.Normal:
        return 'Normal';
      case InquiryUrgency.Urgent:
        return 'Urgent';
      default: {
        const _: never = urgency;
        return _;
      }
    }
  };
  static getInquiryStatusLabel = (inquiryStatus: InquiryStatus) => {
    switch (inquiryStatus) {
      case InquiryStatus.InProgress:
        return 'In progress';
      case InquiryStatus.Confirmed:
        return 'Confirmed';
      case InquiryStatus.Completed:
        return 'Completed';
      case InquiryStatus.Cancelled:
        return 'Cancelled';
      default: {
        const _: never = inquiryStatus;
        return _;
      }
    }
  };

  static parseInjuryJson = (json: string): InquiryJson | null => {
    try {
      const data = JSON.parse(json);
      return data;
    } catch {
      return null;
    }
  };
  static formatDatesRange = (startDate: string, endDate: string) => {
    const start = dayjs.utc(startDate).locale('en');
    const end = dayjs.utc(endDate).locale('en');
    if (!start.isValid() || !end.isValid()) return `${startDate} - ${endDate}`;

    if (start.isSame(end, 'day')) return start.format('D MMMM YYYY');
    if (start.isSame(end, 'month')) return `${start.format('D')} – ${end.format('D MMMM YYYY')}`;
    if (start.isSame(end, 'year')) return `${start.format('D MMMM')} – ${end.format('D MMMM YYYY')}`;
    return `${start.format('D MMMM YYYY')} – ${end.format('D MMMM YYYY')}`;
  };
}
